import type { CityConfig } from "@/pages/city-template/types";

export const astoriaConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — Oregon Vital Records", href: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/", description: "Official Oregon vital records office in Portland" },
    { label: "Marriage Licenses — Clatsop County Clerk", href: "https://www.co.clatsop.or.us/clerk", description: "Clatsop County Clerk — marriage license issuance" },
    { label: "Divorce Records — Clatsop County Clerk", href: "https://www.co.clatsop.or.us/clerk", description: "Clatsop County Clerk — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Oregon Vital Records for online vital record orders" },
  ],
  city: "Astoria",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "astoria",
  route: "/cities/astoria",
  title: "Emergency Passport Astoria OR | Expedited Passport Clatsop County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Astoria, OR — as fast as 24 hours. Fast Passport Center serves Astoria, Clatsop County, Warrenton, Seaside, and the north Oregon coast. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Astoria, Oregon",
  heroSubtitle: "Family cruise to Alaska leaving from Seattle? Last-minute Canada border crossing from the north Oregon coast? Passport expired and your Scandinavian heritage trip is booked? Fast Passport Center helps Astoria residents get expedited passports as fast as 24 hours — entirely remotely. The Seattle Passport Agency is 3 hours south, but we serve you without the trip.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Astoria%20Oregon%20historic%20Victorian%20district%20overlooking%20the%20Columbia%20River%20estuary%20with%20Astoria%20Column%20on%20Coxcomb%20Hill%20visible%20in%20background%2C%20fishing%20boats%20in%20harbor%20at%20golden%20hour%2C%20misty%20Pacific%20Northwest%20coastal%20atmosphere%20with%20lush%20green%20hills%2C%20classic%20maritime%20heritage%20city%20at%20the%20mouth%20of%20the%20Columbia%20River%2C%20dramatic%20overcast%20sky%20with%20golden%20light%20breaking%20through%2C%20photorealistic%20cinematic%20quality%2C%20no%20text%20no%20people&width=1800&height=700&seq=astoria-or-hero-01&orientation=landscape",
  heroImageAlt: "Astoria Oregon Columbia River waterfront with Victorian architecture and Astoria Column",
  geoPosition: "46.1879;-123.8313",
  geoPlacename: "Astoria, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 195 miles north — approximately 3.5 hours by car via US-101 and I-5",
  },
  nearbyCitiesZips: [
    { name: "Astoria", zips: "97103" },
    { name: "Warrenton", zips: "97146" },
    { name: "Seaside", zips: "97138" },
    { name: "Cannon Beach", zips: "97110" },
    { name: "Gearhart", zips: "97138" },
    { name: "Knappa", zips: "97048" },
    { name: "Jewell", zips: "97109" },
    { name: "Hammond", zips: "97121" },
    { name: "Fort Stevens", zips: "97146" },
    { name: "Clatskanie", zips: "97016" },
    { name: "Rainier", zips: "97048" },
    { name: "Longview (WA)", zips: "98632" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Astoria", desc: "Complete your emergency or expedited passport order from anywhere on the north Oregon coast — no road trip to Seattle required." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Astoria or Portland", desc: "Ship your documents from The UPS Store in Astoria or Portland FedEx Office — both support overnight delivery for expedited submissions." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for Clatsop County residents far from major passport acceptance centers." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved Seattle Passport Agency slots — far faster than calling for an emergency appointment." },
  ],
  testimonials: [
    {
      name: "Margaret T.",
      location: "Astoria, OR",
      rating: 5,
      text: "We had a Scandinavian heritage cruise booked and my passport expired. Three weeks out, panicking. Fast Passport Center renewed it in 7 days. They walked me through the overnight shipping from Astoria, kept me updated every day. We made the cruise. Incredible service.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Chris B.",
      location: "Warrenton, OR",
      rating: 5,
      text: "Commercial fishing industry keeps me busy year-round. Needed an emergency passport for a trip to British Columbia for industry meetings. Fast Passport Center did it in 5 business days from Warrenton. No drive to Seattle. Just shipped overnight from Astoria UPS. Phenomenal.",
      service: "Emergency Passport Processing",
    },
    {
      name: "Linda S.",
      location: "Seaside, OR",
      rating: 5,
      text: "Our daughter needed a first passport for a school trip to Canada. I found Fast Passport Center with 10 days to go. They got it done — gave me a perfect checklist for the Seaside acceptance step and expedited from there. My daughter made the trip. Thank you.",
      service: "Child Passport Expedited",
    },
  ],
  facilities: [
    {
      id: "astoria-post-main",
      name: "Astoria Post Office",
      type: "USPS Passport Acceptance",
      address: "748 Commercial St",
      city: "Astoria",
      state: "OR",
      zip: "97103",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Astoria — primary passport acceptance for Astoria and Clatsop County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Astoria residents. Appointment required. Availability can be limited during busy summer tourist season on the north Oregon coast.",
      icon: "ri-mail-line",
    },
    {
      id: "warrenton-post",
      name: "Warrenton Post Office",
      type: "USPS Passport Acceptance",
      address: "720 E Harbor Dr",
      city: "Warrenton",
      state: "OR",
      zip: "97146",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Warrenton — about 5 miles west of Astoria",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Warrenton post office near the Fort Clatsop / Lewis and Clark NHP area. Alternative to Astoria for Warrenton and Hammond residents.",
      icon: "ri-mail-line",
    },
    {
      id: "clatsop-county-clerk",
      name: "Clatsop County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "749 Commercial St",
      city: "Astoria",
      state: "OR",
      zip: "97103",
      phone: "(503) 325-8581",
      hours: "Mon–Fri 8:30am–5pm",
      distance: "Downtown Astoria — Clatsop County Courthouse, adjacent to the Post Office",
      website: "https://www.co.clatsop.or.us/clerk",
      websiteLabel: "Clatsop County Clerk",
      notes: "Clatsop County Clerk in the Courthouse accepts passport applications. Often better appointment availability than the post office, especially in summer.",
      icon: "ri-government-line",
    },
    {
      id: "seaside-post",
      name: "Seaside Post Office",
      type: "USPS Passport Acceptance",
      address: "300 Ave A",
      city: "Seaside",
      state: "OR",
      zip: "97138",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Seaside — about 17 miles south via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Seaside post office is an alternative for Clatsop County residents south of Astoria. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "cannon-beach-post",
      name: "Cannon Beach Post Office",
      type: "USPS Passport Acceptance",
      address: "163 N Hemlock St",
      city: "Cannon Beach",
      state: "OR",
      zip: "97110",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Cannon Beach — about 25 miles south of Astoria via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Very small facility — limited appointment availability. Astoria or Seaside strongly preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "longview-wa-post",
      name: "Longview (WA) Post Office",
      type: "USPS Passport Acceptance",
      address: "1700 Washington Way",
      city: "Longview",
      state: "WA",
      zip: "98632",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Longview WA — about 25 miles east via US-30 and I-5 (cross Astoria-Megler Bridge)",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Longview WA is an alternative facility across the Columbia River — often significantly better appointment availability for urgent Astoria-area passport needs.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Astoria, Oregon?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Astoria residents as fast as 24 hours. The Seattle Passport Agency is about 3.5 hours from Astoria — we serve you entirely remotely via overnight shipping from Astoria UPS or Portland FedEx. Start online, we handle the rest.",
    },
    {
      q: "How fast can I get an expedited passport from Astoria, OR?",
      a: "Expedited passport processing from Astoria takes 2–9 business days through Fast Passport Center. Most adult renewals don't require an in-person acceptance visit — ship overnight from The UPS Store in Astoria or a Portland FedEx Office.",
    },
    {
      q: "Where is the passport acceptance office in Astoria?",
      a: "Astoria's primary passport acceptance locations are the Astoria Post Office (748 Commercial St) and the Clatsop County Clerk's Office (749 Commercial St), both in downtown Astoria. Warrenton and Seaside post offices also accept applications. Longview WA is a recommended alternative with better availability.",
    },
    {
      q: "Do I need a passport for a Canada trip from Astoria?",
      a: "Yes — all U.S. citizens crossing into Canada require a valid passport. From Astoria, BC destinations like Vancouver, Victoria, Whistler, and the Gulf Islands are popular. Fast Passport Center expedites renewals in 2–9 business days for Astoria-area BC travelers.",
    },
    {
      q: "How far is Astoria from the Seattle Passport Agency?",
      a: "Astoria is approximately 195 miles from the Seattle Passport Agency — about 3.5 hours by car via US-101 north and I-5. For life-or-death emergencies requiring same-day processing, a Seattle trip may be necessary. For most urgent travel needs (2–9 business days), Fast Passport Center serves Astoria entirely remotely.",
    },
    {
      q: "Can I renew my passport from Astoria without visiting an office?",
      a: "Yes. Most adult Astoria residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from The UPS Store Astoria or Portland FedEx, and we handle the Seattle agency submission.",
    },
    {
      q: "What airport do Astoria residents use for international travel?",
      a: "Portland International Airport (PDX) is the primary international hub for Astoria travelers — about 100 miles east via US-30 or I-84. PDX serves direct international routes to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City. Seattle-Tacoma (SEA) is also reachable via the Astoria-Megler Bridge and I-5 north.",
    },
    {
      q: "Is Astoria popular for Alaska cruise passengers?",
      a: "Yes — many Astoria and north Oregon coast residents book Alaska Inside Passage cruises departing from Seattle or Vancouver BC. These cruises require a valid passport. Fast Passport Center expedites passports for Astoria-area cruise travelers heading to Alaska.",
    },
    {
      q: "Can I get a child passport from Astoria?",
      a: "Yes. Child passports require an in-person acceptance visit at the Astoria Post Office, Clatsop County Clerk, or Seaside Post Office — both parents must typically be present or provide notarized consent. Fast Passport Center provides the child passport checklist and expedites after the acceptance step.",
    },
    {
      q: "How much does expedited passport processing cost from Astoria?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services. We provide an exact price quote at checkout before any commitment.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Astoria", address: "1015 Marine Dr, Astoria, OR 97103", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "The UPS Store — Astoria", address: "170 W Marine Dr, Astoria, OR 97103", hours: "Mon–Fri 8:30 AM – 6:30 PM, Sat 10 AM – 3 PM", website: "https://www.theupsstore.com/" },
    { name: "Fred Meyer — Seaside", address: "1660 N Roosevelt Dr, Seaside, OR 97138", hours: "Daily 7 AM – 11 PM", website: "https://www.kroger.com/" },
    { name: "Walgreens — Seaside", address: "218 Ave U, Seaside, OR 97138", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "Fred Meyer — Longview WA", address: "600 Triangle Mall, Longview, WA 98632", hours: "Daily 7 AM – 11 PM", website: "https://www.kroger.com/" },
    { name: "Walmart — Warrenton", address: "540 SE Marlin Ave, Warrenton, OR 97146", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "FedEx Office — Portland NW", address: "2208 NW Glisan St, Portland, OR 97210", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM", website: "https://www.fedex.com/" },
    { name: "Costco Photo Center — Warrenton", address: "400 SE Marlin Ave, Warrenton, OR 97146", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM", website: "https://www.costco.com/" },
    { name: "CVS — Longview WA", address: "1609 Washington Way, Longview, WA 98632", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/" },
    { name: "Target Photo — Portland", address: "Multiple Portland locations", hours: "Daily 8 AM – 10 PM", website: "https://www.target.com/" },
  ],
  dmvLocations: [
    {
      name: "Astoria Driver & Motor Vehicle Services",
      address: "1745 Marine Dr, Astoria, OR 97103",
      hours: "Mon–Fri 8:30 AM – 5:00 PM (closed noon–1pm)",
      note: "Astoria DMV — driver licenses, REAL ID, and state ID cards for Clatsop County residents.",
      featured: true,
    },
    {
      name: "Longview WA Department of Licensing",
      address: "1425 Commerce Ave, Longview, WA 98632",
      hours: "Mon–Fri 8:30 AM – 4:30 PM",
      note: "Washington state DOL in Longview — alternative for WA-licensed residents in the Astoria area.",
      featured: false,
    },
    {
      name: "Tillamook Driver & Motor Vehicle Services",
      address: "910 SE Pacific Ave, Tillamook, OR 97141",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Tillamook DMV — serves south Clatsop and north Tillamook County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international hub — about 100 miles east via US-30 or US-26 and I-84 (1.5–2 hours)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the primary international departure hub for Astoria travelers. PDX offers direct flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City — a 1.5–2 hour drive from Astoria.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "About 195 miles north via US-101 and I-5 (3.5 hours)",
      address: "17801 International Blvd, Seattle, WA 98158",
      note: "SEA-TAC is an alternative major international hub for Astoria travelers with more flight options, especially for Asia, Alaska, and European routes. Accessible via the Astoria-Megler Bridge north to I-5.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA-TAC Airport Website",
      featured: false,
    },
    {
      code: "OTH",
      name: "Southwest Oregon Regional Airport",
      distance: "About 250 miles south (not primary — use PDX or SEA)",
      address: "1100 Airport Ln, North Bend, OR 97459",
      note: "Regional airport in North Bend/Coos Bay — domestic connections only. Portland International is strongly preferred for all Astoria international travel.",
      flightUrl: "https://www.cooscountyairportdistrict.com/",
      flightLabel: "OTH Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Astoria & North Oregon Coast",
      description: "Alaska cruise departure imminent? Canada border crossing emergency? Scandinavian heritage trip booked? Fast Passport Center handles emergency passport processing for Astoria and Clatsop County residents — as fast as 24 hours. Serve entirely remotely — no Seattle road trip.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during remote north coast years? Astoria residents renew fast through Fast Passport Center — ship from Astoria UPS or Portland FedEx overnight, we handle the expedited submission from there.",
      bullets: ["Fastest renewal for Astoria residents", "Ship overnight from Astoria or Portland", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Astoria or before an international trip? We guide Clatsop County travelers through DS-64 and expedited replacement — no Seattle trip required.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Astoria Families",
      description: "Astoria families heading to British Columbia, Alaska cruises, or international travel need child passports. We guide you through the Astoria Post Office or Clatsop County Clerk acceptance step.",
      bullets: ["Both parents typically required to consent", "Astoria or Clatsop County Clerk guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Astoria? In-person acceptance at the Astoria Post Office or County Clerk is required for first-timers. We guide you through every step and expedite from there.",
      bullets: ["Step-by-step acceptance guidance for Astoria facilities", "Photo location guide for Astoria and Warrenton area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for fishing industry professionals, maritime workers, and frequent travelers in the Astoria area.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for fishing industry travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Astoria, Oregon residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Astoria and Clatsop County residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Astoria, Oregon — ship overnight from Astoria UPS or Portland FedEx." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Astoria travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Astoria and Clatsop County families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Astoria, Oregon and the north Oregon coast." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking Alaska Cruises Without Checking Passport Validity", description: "Alaska Inside Passage cruises from Seattle or Vancouver BC are incredibly popular with Astoria area residents. Many book the cruise months in advance but forget to check passport expiration until final payment is due — often 90 days before departure." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from drugstores or self-taken phone photos frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks. Our pre-submission review catches all errors before they cause delays." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-map-pin-time-line", title: "Ignoring the Six-Month Validity Rule for Canada", description: "Canada entry requires your passport to be valid through your trip. While Canada technically only requires validity through your stay, airlines and cruise lines often apply the six-month rule. Check expiration before booking any BC trip." },
    { icon: "ri-calendar-close-line", title: "Underestimating Astoria's Remote Location from Passport Agencies", description: "Astoria is 195 miles from Seattle and 100 miles from Portland — making last-minute acceptance office runs difficult. Start the passport process as early as possible and use Fast Passport Center's overnight shipping to avoid stressful drives." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Astoria travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Astoria and Clatsop County acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at The UPS Store in Astoria or Portland FedEx Office for overnight delivery. Both are reliable options for north coast residents." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — bypassing the public appointment backlog that can stretch for weeks." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your Astoria or north Oregon coast address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Astoria or Portland",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 3.5 hours from Astoria",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Long wait times even with appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; can be longer in summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, Seattle only", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to Seattle — 3.5 hours from Astoria", fpc: "Ship overnight from Astoria or Portland" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Oregon Coast Accessibility", agency: "Not designed for remote coastal residents", fpc: "Fully remote service via overnight shipping" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 7-hour Seattle round trip cost", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Seaside", route: "/cities/seaside" },
    { name: "Emergency passport help in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Lincoln City", route: "/cities/lincoln-city" },
    { name: "Rush passport services for Oregon residents", route: "/states/oregon" },
    { name: "Expedited passports statewide in Oregon", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from The UPS Store in Astoria or a Portland FedEx Office — both available weekday evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving Astoria and the entire north Oregon coast remotely",
  serviceAreaIntro: "Whether you are in Astoria, Warrenton, Seaside, Cannon Beach, Gearhart, or anywhere in Clatsop County — Fast Passport Center makes emergency and expedited passport processing simple without a drive to Seattle.",
  nearbyCitiesIntro: "We serve all Clatsop County zip codes and surrounding communities including Warrenton, Seaside, Cannon Beach, Gearhart, Hammond, Knappa, Clatskanie, Rainier, and Longview WA.",
  howItWorksIntro: "Getting an emergency or expedited passport from Astoria is simpler than a 3.5-hour drive to Seattle. Most north Oregon coast clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Astoria and Clatsop County passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Astoria, Warrenton, and north Oregon coast travelers who needed their passports fast.",
  faqIntro: "The most common questions Astoria and Clatsop County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Astoria and North Oregon Coast Residents",
    intro: "Astoria is Oregon's oldest city — a maritime heritage hub at the mouth of the Columbia River, surrounded by Clatsop State Forest and the Pacific. Its geography creates very specific international travel patterns for north coast residents: Alaska and BC cruises, Scandinavian and European heritage travel, and Canada border crossings.",
    cards: [
      {
        icon: "ri-ship-2-line",
        title: "Alaska Inside Passage Cruises from Seattle or Vancouver BC",
        subtitle: "The most common international travel urgency for Astoria-area residents",
        description: "Alaska Inside Passage cruises are enormously popular with Astoria and north Oregon coast residents — departing from Seattle (3.5 hours north) or Vancouver BC (4.5 hours north). These cruises require a valid U.S. passport even if you return to a U.S. port. Bookings are often made 6–12 months in advance, but passport checks happen at the last minute — often at final payment or when printing documents. Fast Passport Center expedites passports for Astoria-area cruise travelers in 2–9 business days.",
        tip: "Alaska cruise bookings often happen in September for the following summer. Check passport expiration dates the same day you book. If your passport expires before October of the cruise year, renew it immediately — expedited processing from Astoria takes 2–9 business days.",
      },
      {
        icon: "ri-global-line",
        title: "Scandinavian & European Heritage Travel",
        subtitle: "Astoria's strong Nordic heritage community creates unique European passport urgency",
        description: "Astoria has a deep Scandinavian and Finnish heritage — the city was founded by Finnish and Norwegian immigrants and retains a strong Nordic identity. Heritage travel to Scandinavia (Norway, Sweden, Finland, Denmark) is common among Astoria families — for reunion trips, heritage tours, and Nordic festivals. These trips are often planned for summer, meaning passport discovery and application happen in February through April. European heritage trips also include northern Europe — Ireland, Scotland, and the Netherlands for Dutch-descended Astoria families.",
        tip: "Scandinavian summer daylight trips (June–August) should have passport applications started by March at the latest. Schengen Area countries in Europe require passports valid 3 months beyond your return date. Fast Passport Center expedites renewals from Astoria in 2–9 business days.",
      },
      {
        icon: "ri-map-pin-2-line",
        title: "British Columbia and Pacific Northwest Canada Travel",
        subtitle: "BC is the closest international destination for Astoria residents",
        description: "British Columbia is Astoria's most accessible international destination — Vancouver is about 4 hours north, Victoria and Gulf Islands are reachable by ferry from Tsawwassen, and Whistler is a popular ski destination. Passport requirements for Canadian entry by air are strict — even for a quick Vancouver weekend. The Astoria-Megler Bridge puts Washington state within reach, and BC entry points at Blaine/Peace Arch are popular. Fast Passport Center handles expedited passports for BC travelers from Astoria.",
        tip: "Canada requires a valid passport for air entry and for entries requiring NEXUS if you don't have NEXUS. For road crossings, REAL ID is accepted but a passport book or card is strongly recommended. Check your passport's expiration before booking any BC trip.",
      },
      {
        icon: "ri-anchor-line",
        title: "Commercial Fishing & Maritime Industry Travel",
        subtitle: "Astoria's commercial fishing fleet travels internationally for industry meetings and operations",
        description: "Astoria is Oregon's major commercial fishing port — one of the largest on the Pacific coast. Commercial fishing industry professionals travel internationally to Alaska (permits, processing operations), British Columbia (joint management agreements), Pacific Rim ports, and international fisheries conferences. Maritime industry travel can arise suddenly — an unexpected meeting in Victoria BC or a fleet deployment that requires a passport with little notice. Fast Passport Center handles rapid expedited passports for Astoria fishing industry professionals.",
        tip: "Commercial fishing industry professionals in Astoria should maintain a passport with at least 18 months of validity at all times. Industry travel to BC, Alaska, and Pacific Rim ports can arise with minimal notice — last-minute emergency passports are available through Fast Passport Center but planning ahead is always better.",
      },
    ],
    callout: {
      icon: "ri-anchor-line",
      title: "Astoria, Oregon — Where the Columbia Meets the Pacific",
      description: "Astoria sits at the intersection of Pacific maritime culture, Scandinavian heritage, and Pacific Northwest adventure. The city's unique identity — oldest in Oregon, strong Nordic roots, active fishing port, and Alaska/BC gateway — creates a distinctive set of international travel patterns. Fast Passport Center has served Astoria and Clatsop County residents with expedited passports for Scandinavian heritage trips, Alaska cruises, BC adventures, and commercial fishing industry travel.",
    },
  },
  destinations: {
    title: "Where Astoria Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Astoria and north Oregon coast clients. Scandinavian heritage, Alaska cruise culture, BC proximity, and the commercial fishing industry create unique passport urgency situations for Clatsop County residents.",
    stats: [
      { stat: "195 mi", label: "to Seattle Passport Agency — the closest regional passport office from Astoria" },
      { stat: "100 mi", label: "to Portland International Airport (PDX) — primary international hub" },
      { stat: "4 hrs", label: "to Vancouver BC — Astoria's most accessible international destination" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Astoria" },
    ],
    destinations: [
      { name: "Alaska — Inside Passage Cruises", context: "Seattle and Vancouver BC cruise departures — Alaska's Inside Passage is the #1 international trip for Astoria area residents", urgency: "Cruise bookings 6–12 months ahead; passport issues discovered at final payment or document printing time", icon: "ri-ship-2-line" },
      { name: "British Columbia — Vancouver, Victoria, Whistler", context: "Pacific Northwest's closest international destination — 4 hours north via US-101 and I-5 to Blaine", urgency: "BC ski season booked in November; BC summer trips booked in March — passport discovery happens at booking time", icon: "ri-map-pin-2-line" },
      { name: "Scandinavia — Norway, Sweden, Finland", context: "Heritage travel for Astoria's deep Nordic community — family reunions, heritage tours, Norse cultural festivals", urgency: "Summer Scandinavia trips planned in February; passport applications started in March with 3–4 months lead time needed", icon: "ri-global-line" },
      { name: "Mexico — Puerto Vallarta, Cabo, Riviera Maya", context: "Winter warm-weather escapes for north Oregon coast residents — November through April Mexican beach and culture travel", urgency: "October–November Mexico booking; passport expiration discovered when gathering documents for January departure", icon: "ri-sun-cloudy-line" },
      { name: "Ireland & Scotland", context: "Celtic heritage travel for Astoria's Irish and Scottish community — family genealogy trips and cultural tours", urgency: "Summer 'roots' trips booked in February–March; passport renewals needed in March–April for June departure", icon: "ri-map-pin-2-line" },
      { name: "Japan & Pacific Rim", context: "Commercial fishing industry travel to Pacific Rim ports and fisheries conferences; cultural travel from Astoria's Japanese-American community", urgency: "Industry meetings can require rapid passport processing with 1–2 weeks notice — emergency processing available from Astoria", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Oregon coast and northwest Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Portland", route: "/cities/portland" },
      { name: "Seaside", route: "/cities/seaside" },
      { name: "Lincoln City", route: "/cities/lincoln-city" },
    ],
  },
};